/**
 * What a vendor's payload must look like, and what they are told when it does not.
 *
 * A document's own description says what the fields are called and roughly what type they are. It
 * never says what *this* vendor actually sends: that their dates arrive as `06/09/2024`, that they
 * leave `storeCode` out on web orders, that their quantities come as strings. That is knowledge
 * about an integration, and it belongs to whoever set the integration up.
 *
 * So a contract is configured, not imported — on the node that already validates, at whichever
 * scope it belongs to. One vendor's contract covers all their tenants and businesses until
 * somebody opens a tenant, or one business, and changes it there.
 *
 * Every refusal names the field and shows the value, because a vendor can only fix what they can
 * see. Nothing here describes the inside of the platform, so all of it is safe to send back.
 */

import { schemaDocument } from './schema';
import { getValue } from './transform';

export type ContractType = 'string' | 'integer' | 'number' | 'boolean' | 'date' | 'object' | 'array';

/**
 * A date shape this vendor may send: what to call it, and how to recognise it.
 *
 * The pattern travels with the rule rather than living in either engine, so a shape nobody
 * anticipated is configured the same way as a common one — and a refusal can still say
 * `dd/MM/yyyy` instead of a regular expression.
 */
export interface DateShape {
  name: string;
  pattern: string;
}

/**
 * Shapes offered as a starting point. Nothing is limited to them: a person types a name and a
 * pattern and gets a rule of their own, which both the editor and the runtime honour.
 */
export const DATE_FORMATS = {
  'iso-8601': {
    label: 'ISO-8601 (2024-09-06T12:37:04Z)',
    test: /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:?\d{2})?)?$/,
  },
  'yyyy-MM-dd': { label: 'yyyy-MM-dd (2024-09-06)', test: /^\d{4}-\d{2}-\d{2}$/ },
  'dd/MM/yyyy': { label: 'dd/MM/yyyy (06/09/2024)', test: /^\d{2}\/\d{2}\/\d{4}$/ },
  'MM/dd/yyyy': { label: 'MM/dd/yyyy (09/06/2024)', test: /^\d{2}\/\d{2}\/\d{4}$/ },
  'dd-MM-yyyy': { label: 'dd-MM-yyyy (06-09-2024)', test: /^\d{2}-\d{2}-\d{4}$/ },
  'yyyy/MM/dd': { label: 'yyyy/MM/dd (2024/09/06)', test: /^\d{4}\/\d{2}\/\d{2}$/ },
  'epoch-millis': { label: 'Milliseconds since 1970', test: /^\d{10,16}$/ },
  'epoch-seconds': { label: 'Seconds since 1970', test: /^\d{9,11}$/ },
} as const;

export type DateFormat = keyof typeof DATE_FORMATS;

/** The offered shapes, as data: a suggestion list for the editor, nothing the engines depend on. */
export const DATE_SUGGESTIONS: DateShape[] = (Object.keys(DATE_FORMATS) as DateFormat[]).map((name) => ({
  name,
  pattern: DATE_FORMATS[name].test.source,
}));

/** The types a field can be held to. One list, so the editor and the check can never disagree. */
export const CONTRACT_TYPES: ContractType[] = ['string', 'integer', 'number', 'boolean', 'date', 'object', 'array'];

/**
 * Every rule that can be set, as data.
 *
 * The editor draws itself from this and the engines read the fields it names. Adding a rule is one
 * entry here plus its check — never a new screen, and never a list of options written into a form.
 */
export interface RuleDefinition {
  key: 'dateFormats' | 'range' | 'maxLength' | 'oneOf' | 'pattern' | 'entries';
  label: string;
  /** Which types it means anything for. */
  types: ContractType[];
  /** How the editor should ask for it. */
  input: 'tokens' | 'pair' | 'number' | 'text';
}

export const RULE_CATALOGUE: RuleDefinition[] = [
  { key: 'dateFormats', label: 'Allowed formats', types: ['date'], input: 'tokens' },
  { key: 'oneOf', label: 'Only these values', types: ['string'], input: 'tokens' },
  { key: 'maxLength', label: 'Longest allowed', types: ['string'], input: 'number' },
  { key: 'pattern', label: 'Must look like', types: ['string'], input: 'text' },
  { key: 'range', label: 'Smallest and largest', types: ['integer', 'number'], input: 'pair' },
  { key: 'entries', label: 'How many entries', types: ['array'], input: 'pair' },
];

export interface ContractField {
  /** Dotted, with `[]` where a list is walked: `orderItems[].total`. */
  path: string;
  type: ContractType;
  /** Refused when absent, null, or an empty string. */
  required?: boolean;
  /**
   * Every date shape this vendor may send. Only these are accepted.
   *
   * Each entry carries its own pattern, so nothing about formats is fixed in either engine. A
   * plain string is read too, for contracts saved before shapes carried their pattern.
   */
  dateFormats?: (DateShape | string)[];
  /** Kept for contracts saved before a pattern was just another entry in the list above. */
  datePattern?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  pattern?: string;
  oneOf?: string[];
  /**
   * The kinds of document this rule is about, and the field that says which kind one is.
   *
   * One endpoint takes more than one kind of document, and they do not agree on what has to be
   * there: an invoice has payment splits and lines, a cancellation has neither and is not wrong
   * for that. Without this a contract can only hold a vendor to what every kind shares, which is
   * the least of what any of them should be held to.
   *
   * Left out, the rule applies to everything, which is what most fields want.
   */
  when?: { field: string; equals: string[] };
  minItems?: number;
  maxItems?: number;
}

export interface Contract {
  version: 1;
  fields: ContractField[];
}

/** One thing wrong with a payload, in the words the vendor is sent. */
export interface Violation {
  path: string;
  code: 'missing' | 'type' | 'date-format' | 'range' | 'too-long' | 'pattern' | 'not-allowed' | 'too-few' | 'too-many';
  message: string;
}

export const CONTRACT_RESOURCE = 'contract.json';

export const emptyContract = (): Contract => ({ version: 1, fields: [] });

/** Values that count as not being there at all: absent, null, or blank. */
const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** What the payload holds at one path, once for a plain field and once per entry for a list. */
const valuesAt = (payload: unknown, path: string): { at: string; value: unknown }[] => {
  const walk = (node: unknown, parts: string[], seen: string): { at: string; value: unknown }[] => {
    if (parts.length === 0) return [{ at: seen, value: node }];
    const [part, ...rest] = parts;
    const list = part.endsWith('[]');
    const key = list ? part.slice(0, -2) : part;
    const child = node && typeof node === 'object' ? (node as Record<string, unknown>)[key] : undefined;
    const here = seen ? `${seen}.${key}` : key;

    if (!list) return walk(child, rest, here);
    if (!Array.isArray(child)) return child === undefined ? [] : [{ at: here, value: child }];

    return child.flatMap((entry, index) => walk(entry, rest, `${here}[${index}]`));
  };

  return walk(payload, path.split('.').filter(Boolean), '');
};

/**
 * A value as the vendor sees it in the message.
 *
 * Text is quoted and everything else is not, which is the difference the message is usually
 * about: `'3'` is a string where a number was expected, and `3.5` is a number that is not whole.
 */
const show = (value: unknown): string => {
  if (typeof value === 'string') return `'${value.slice(0, 60)}'`;
  const text = JSON.stringify(value);

  return text === undefined ? 'nothing' : String(text).slice(0, 60);
};

/** What the rule is called, for the message: the name if it has one, else the pattern itself. */
const shapeName = (entry: DateShape | string): string => (typeof entry === 'string' ? entry : entry.name);

const formatNames = (field: ContractField): string =>
  [...(field.dateFormats ?? []).map(shapeName), ...(field.datePattern ? [field.datePattern] : [])].join(', ');

/** Whether a value is one of the date shapes this field allows. Nothing else is accepted. */
const matchesPattern = (pattern: string, text: string): boolean => {
  try {
    return new RegExp(pattern).test(text);
  } catch {
    /* a pattern that does not compile matches nothing, rather than throwing at a vendor */
    return false;
  }
};

const isAllowedDate = (value: unknown, field: ContractField): boolean => {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  if (!text) return false;

  const shapes: (DateShape | string)[] = [
    ...(field.dateFormats ?? []),
    ...(field.datePattern ? [field.datePattern] : []),
  ];

  return shapes.some((entry) => {
    if (typeof entry !== 'string') return matchesPattern(entry.pattern, text);
    /* an older contract: a name we still know, or a pattern somebody typed */
    const named = DATE_FORMATS[entry as DateFormat];

    return named ? named.test.test(text) : matchesPattern(entry, text);
  });
};

const typeHolds = (value: unknown, type: ContractType): boolean => {
  if (type === 'string') return typeof value === 'string';
  if (type === 'boolean') return typeof value === 'boolean';
  if (type === 'object') return !!value && typeof value === 'object' && !Array.isArray(value);
  if (type === 'array') return Array.isArray(value);
  if (type === 'integer') return typeof value === 'number' && Number.isInteger(value);
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);

  return true;
};

const article = (type: ContractType): string => (type === 'integer' || type === 'object' ? 'an' : 'a');

/**
 * Everything wrong with this payload, in the order the fields were configured.
 *
 * All of them, not the first: a vendor fixing one field at a time, one request at a time, is the
 * slowest possible way to onboard, and nothing is gained by holding the rest back.
 *
 * A field that was not configured is not an error. A vendor adding something to their payload
 * should never break an integration that was working.
 */
/**
 * Whether this rule has anything to say about this document.
 *
 * A rule narrowed to a kind is silent on every other kind — not satisfied by it, silent on it —
 * so a cancellation is never asked for the payment splits an invoice must carry.
 */
export const appliesTo = (field: ContractField, payload: unknown): boolean => {
  const when = field.when;
  if (!when?.field || !(when.equals ?? []).length) return true;

  const held = getValue(payload, when.field);
  if (held === undefined || held === null) return false;
  const actual = String(held).toLowerCase();

  return when.equals.some((wanted) => String(wanted).toLowerCase() === actual);
};

export const checkContract = (contract: Contract, payload: unknown): Violation[] => {
  const violations: Violation[] = [];

  for (const field of contract.fields) {
    if (!appliesTo(field, payload)) continue;
    for (const { at, value } of valuesAt(payload, field.path)) {
      if (isBlank(value)) {
        if (field.required) violations.push({ path: at, code: 'missing', message: `${at} is required` });
        continue;
      }

      if (field.type === 'date') {
        if (!isAllowedDate(value, field))
          violations.push({
            path: at,
            code: 'date-format',
            message: `${at}: ${show(value)} is not one of the allowed formats (${formatNames(field) || 'none configured'})`,
          });
        continue;
      }

      if (!typeHolds(value, field.type)) {
        violations.push({
          path: at,
          code: 'type',
          message: `${at}: ${show(value)} is not ${article(field.type)} ${field.type}`,
        });
        continue;
      }

      if (typeof value === 'number') {
        if (field.min !== undefined && value < field.min)
          violations.push({ path: at, code: 'range', message: `${at}: ${value} is below the minimum ${field.min}` });
        if (field.max !== undefined && value > field.max)
          violations.push({ path: at, code: 'range', message: `${at}: ${value} is above the maximum ${field.max}` });
      }

      if (typeof value === 'string') {
        if (field.maxLength !== undefined && value.length > field.maxLength)
          violations.push({
            path: at,
            code: 'too-long',
            message: `${at}: ${value.length} characters, and at most ${field.maxLength} are allowed`,
          });
        if (field.oneOf?.length && !field.oneOf.includes(value))
          violations.push({
            path: at,
            code: 'not-allowed',
            message: `${at}: ${show(value)} is not one of ${field.oneOf.join(', ')}`,
          });
        if (field.pattern) {
          try {
            if (!new RegExp(field.pattern).test(value))
              violations.push({
                path: at,
                code: 'pattern',
                message: `${at}: ${show(value)} is not in the expected form`,
              });
          } catch {
            /* an unusable pattern refuses nothing, rather than refusing everything */
          }
        }
      }

      if (Array.isArray(value)) {
        if (field.minItems !== undefined && value.length < field.minItems)
          violations.push({
            path: at,
            code: 'too-few',
            message: `${at}: ${value.length} entries, and at least ${field.minItems} are needed`,
          });
        if (field.maxItems !== undefined && value.length > field.maxItems)
          violations.push({
            path: at,
            code: 'too-many',
            message: `${at}: ${value.length} entries, and at most ${field.maxItems} are allowed`,
          });
      }
    }
  }

  return violations;
};

/* ── the shape, as a tree ────────────────────────────────────────────────────────────────── */

/**
 * The same rules arranged the way a payload is actually shaped.
 *
 * Storage keeps a flat list of paths, because that is what the runtime walks and what a refusal
 * names — `orderItems[0].total` tells a vendor exactly which entry was wrong. Nobody should have
 * to read that while configuring, though: a payload is a tree, so it is edited as one.
 */
export interface ContractNode {
  /** The stored path this node stands for. Empty for the level inside a list. */
  path: string;
  /** What to call it on screen. A list's contents are one level called "each entry". */
  name: string;
  kind: 'field' | 'entry';
  /** The rules configured here, when this node is a field somebody has described. */
  field?: ContractField;
  children: ContractNode[];
}

/** `orderItems[].tax.rate` → orderItems → each entry → tax → rate. */
const segmentsOf = (path: string): { key: string; list: boolean }[] =>
  path
    .split('.')
    .filter(Boolean)
    .map((part) => ({ key: part.endsWith('[]') ? part.slice(0, -2) : part, list: part.endsWith('[]') }));

export const contractTree = (fields: ContractField[]): ContractNode[] => {
  const roots: ContractNode[] = [];

  for (const field of fields) {
    let level = roots;
    let walked = '';

    for (const [index, segment] of segmentsOf(field.path).entries()) {
      walked = walked ? `${walked}.${segment.key}` : segment.key;
      const last = index === segmentsOf(field.path).length - 1;

      let node = level.find((candidate) => candidate.kind === 'field' && candidate.path === walked);
      if (!node) {
        node = { path: walked, name: segment.key, kind: 'field', children: [] };
        level.push(node);
      }
      if (last) node.field = field;

      if (segment.list) {
        walked = `${walked}[]`;
        let entry = node.children.find((candidate) => candidate.kind === 'entry');
        if (!entry) {
          entry = { path: walked, name: 'each entry', kind: 'entry', children: [] };
          node.children.push(entry);
        }
        level = entry.children;
      } else {
        level = node.children;
      }
    }
  }

  return roots;
};

/** How many rules are set on a field, so a tree row can say whether it has been configured. */
export const ruleCount = (field?: ContractField): number => {
  if (!field) return 0;
  const counted = [
    field.required,
    field.dateFormats?.length,
    field.datePattern,
    field.min,
    field.max,
    field.maxLength,
    field.pattern,
    field.oneOf?.length,
    field.minItems,
    field.maxItems,
  ];

  return counted.filter((value) => value !== undefined && value !== false && value !== 0).length;
};

/* ── starting from a payload the vendor really sent ──────────────────────────────────────── */

/**
 * Which date shapes a value looks like, for a first guess.
 *
 * Epoch formats are deliberately never guessed. A ten digit run is a phone number about as often
 * as it is a moment in time, and calling `customerMobile` a date is a worse start than calling a
 * real timestamp a string — one is corrected in a second, the other is believed.
 */
const looksLikeDate = (value: string): DateShape[] =>
  DATE_SUGGESTIONS.filter((shape) => !shape.name.startsWith('epoch-')).filter((shape) =>
    new RegExp(shape.pattern).test(value.trim()),
  );

/**
 * A contract inferred from one real payload, as a starting point.
 *
 * It is a guess and is meant to be corrected: nothing is marked required, because one payload
 * cannot say what is always there, and a string that happens to parse as a date is offered as a
 * date with the formats it matched. The person configuring it knows which of those is true.
 */
export const inferContract = (payload: unknown, limit = 300): Contract => {
  const fields: ContractField[] = [];

  const walk = (node: unknown, prefix: string, depth: number): void => {
    if (fields.length >= limit || depth > 8 || node === null || node === undefined) return;

    if (Array.isArray(node)) {
      if (prefix) fields.push({ path: prefix, type: 'array' });
      walk(node[0], prefix ? `${prefix}[]` : '', depth + 1);

      return;
    }

    if (typeof node === 'object') {
      if (prefix && !prefix.endsWith('[]')) fields.push({ path: prefix, type: 'object' });
      for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
        walk(child, prefix ? `${prefix}.${key}` : key, depth + 1);
      }

      return;
    }

    if (!prefix) return;
    if (typeof node === 'boolean') fields.push({ path: prefix, type: 'boolean' });
    else if (typeof node === 'number')
      fields.push({ path: prefix, type: Number.isInteger(node) ? 'integer' : 'number' });
    else if (typeof node === 'string') {
      const formats = looksLikeDate(node);
      fields.push(
        formats.length > 0 ? { path: prefix, type: 'date', dateFormats: formats } : { path: prefix, type: 'string' },
      );
    }
  };

  /* A list of documents describes one document, which is what a contract is about. */
  walk(Array.isArray(payload) ? payload[0] : payload, '', 0);

  return { version: 1, fields };
};

/** Reads a stored contract, refusing anything that is not one rather than half-applying it. */
export const readContract = (raw: string | undefined): Contract | undefined => {
  if (!raw?.trim()) return undefined;

  try {
    const parsed = JSON.parse(raw) as Contract;
    if (!Array.isArray(parsed?.fields)) return undefined;

    return { version: 1, fields: parsed.fields.filter((field) => typeof field?.path === 'string' && field.path) };
  } catch {
    return undefined;
  }
};

/* ── the same shape, for the DataMapper's source tree ───────────────────────────────────────── */

/**
 * What a vendor may send, as the JSON Schema the DataMapper reads.
 *
 * The contract is the one place this platform holds a deliberate, typed description of a
 * vendor's payload — required fields, real types, date shapes. The mapper's source tree used to
 * be guessed from captured samples instead, which never knew a field was required and never told
 * a date apart from a string. Deriving the tree from the contract means describing the payload
 * once, here, and having both the door and the mapper agree with it.
 *
 * JSON Schema has no `date` type of its own, so a date field becomes a string carrying
 * `format: "date"` — enough for the mapper to show it as a date without inventing a type Kaoto
 * would not recognise.
 */
export const contractSchema = (contract: Contract): Record<string, unknown> | null => {
  if (contract.fields.length === 0) return null;

  const typed = contract.fields.map((field) => ({
    path: field.path,
    type: field.type === 'date' ? 'string' : field.type,
    required: field.required === true,
  }));

  const document = schemaDocument({ id: '', code: '', version: contract.version, title: '', fields: typed });
  if (!document) return null;

  for (const field of contract.fields) {
    if (field.type !== 'date') continue;
    const node = nodeAt(document, field.path);
    if (node) node.format = 'date';
  }

  return document;
};

/** Walks a JSON Schema document to the node a stored path names, following `[]` into `items`. */
const nodeAt = (document: Record<string, unknown>, path: string): Record<string, unknown> | undefined => {
  let node: Record<string, unknown> = document;

  for (const part of path.split('.').filter(Boolean)) {
    const list = part.endsWith('[]');
    const key = list ? part.slice(0, -2) : part;
    const properties = node.properties as Record<string, Record<string, unknown>> | undefined;
    const child = properties?.[key];
    if (!child) return undefined;
    node = list ? ((child.items as Record<string, unknown>) ?? child) : child;
  }

  return node;
};

/**
 * Which schema the mapper's source tree should actually show.
 *
 * The contract wins whenever somebody has described anything in it — it is the more exact of the
 * two, and the whole point of describing a field once. A route nobody has configured a contract
 * for keeps reading its shape from whatever samples were captured, exactly as it always has.
 */
export const preferredSourceSchema = (
  contractText: string | undefined,
  sampleSchema: Record<string, unknown>,
): Record<string, unknown> => {
  const contract = readContract(contractText);
  const fromContract = contract ? contractSchema(contract) : null;

  return fromContract ?? sampleSchema;
};
